import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'

import { getLocalStorageItem, setLocalStorageItem } from '~/libs/localStorage'
import { parseJepxAreaCodes } from '~/models/area'

/**
 * 入札一覧の左フィルターサイドバーを開いているか (端末単位、localStorage)。
 *
 * プロトタイプ (`design/bidding/spot.html`) は `body.spot-sidebar-on` 1 つを
 * 通常入札 / ブロック入札の両タブで共有し、状態を `localStorage` に持つ。
 * 実装もタブ間で共有する (= 片方で閉じてタブを切り替えたら開いて見える、を避ける)。
 *
 * 読み書きとも **best-effort**。 private mode / quota 超過で例外になっても
 * 既定 (開いている) に倒すだけで、画面は動く。
 */
const SIDEBAR_OPEN_KEY = 'ebalance-spot-sidebar-on'

/** 既定は「開いている」。 これまでの挙動 (常時表示) を初期状態にする。 */
export const loadSpotSidebarOpen = (): boolean => {
  try {
    const raw = localStorage.getItem(SIDEBAR_OPEN_KEY)
    if (raw === null) return true
    return raw === '1'
  } catch {
    return true
  }
}

export const saveSpotSidebarOpen = (open: boolean): void => {
  try {
    localStorage.setItem(SIDEBAR_OPEN_KEY, open ? '1' : '0')
  } catch {
    // 保存できない環境ではセッション内の開閉だけ効く (既定へ戻る)。
  }
}

/**
 * 入札一覧のエリアフィルタ (AC-BSP-019) の選択 (端末単位、localStorage)。
 *
 * 共通 spec AC-SC-031 [P0]「フィルタバーを持つ全画面で選択状態を保持し、再訪時に
 * 復元する」 による (#10122)。 受渡日は AC-BSP-043 で毎回翌日に戻すため対象外。
 *
 * 保存するのは**ユーザーが選んだ値だけ**。 取引不可になったエリアを画面側が選択から
 * 落とした結果は保存しない (受渡日ごとに取引可能エリアが変わるため、 落とした値を
 * 保存すると、 取引できる日に戻っても選択が戻らない)。
 */
const AREA_FILTER_KEY = 'ebalance-spot-area-filter'

/** 未保存・不正値は undefined (= 呼び出し側の既定値を使う)。 空配列は「全エリア」 の選択として返す。 */
export const loadSpotAreaFilter = (): Set<AreaCode> | undefined => {
  const raw = getLocalStorageItem(AREA_FILTER_KEY)
  const areas = raw === undefined ? undefined : parseJepxAreaCodes(raw)
  return areas === undefined ? undefined : new Set(areas)
}

export const saveSpotAreaFilter = (areas: ReadonlySet<AreaCode>): void => {
  setLocalStorageItem(AREA_FILTER_KEY, JSON.stringify([...areas]))
}
